import { OpeningAtRiskResponse } from 'api-clients/monolith';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { Suspense, useId, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import GreyDotIcon from '@/assets/icons/grey-dot.svg';
import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import {
  HiringGoalDetails,
  OpeningDetails,
} from '@/components/pages/recommendation-posting-dialog/types';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';

import { BudgetRecommendation } from './budget-recommendation';
import { GeneralRecommendation } from './general-recommendation';

interface OpeningItemProps {
  opening: OpeningAtRiskResponse['data'][number];
  isFirst: boolean;
}

export function OpeningItem({ opening, isFirst }: OpeningItemProps) {
  const [isExpanded, setIsExpanded] = useState(isFirst);
  const recommendationsPanelId = useId();

  const openingDetails: OpeningDetails = {
    title: opening.title,
    slug: opening.slug,
    externalId: opening.external_id,
  };

  const hiringGoalDetails: HiringGoalDetails = {
    hireBy: opening.hire_by,
    duration: opening.duration,
    daysElapsed: opening.days_elapsed,
  };

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        className="hover:bg-fx-neutral-50 focus-visible:ring-fx-primary flex w-full cursor-pointer items-center justify-between gap-4 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        onClick={handleExpand}
        aria-expanded={isExpanded}
        aria-controls={
          isExpanded && opening.slug ? recommendationsPanelId : undefined
        }
      >
        <div className="flex flex-col gap-2 py-2">
          {/* Opening name */}
          <Typography variant="body2">{opening.title}</Typography>

          <div className="flex items-center gap-3">
            {/* Days elapsed / duration */}
            <Typography variant="body1" className="!text-fx-neutral-600">
              <FormattedMessage
                defaultMessage="{daysElapsed} / {duration} days remaining"
                description="Progress toward hiring goal window: elapsed days vs total window for this opening"
                values={{
                  daysElapsed: opening.days_elapsed,
                  duration: opening.duration,
                }}
              />
            </Typography>

            <img src={GreyDotIcon} alt="Separator" className="size-3" />

            {/* Target applicant count / applied applicant count */}
            <Typography variant="body1" className="!text-fx-neutral-600">
              <FormattedMessage
                defaultMessage="{appliedApplicantCount} / {targetApplicantCount} applicants"
                description="Applicant funnel counts vs hiring goal target for this opening"
                values={{
                  appliedApplicantCount: opening.applied_applicant_count,
                  targetApplicantCount: opening.target_applicant_count,
                }}
              />
            </Typography>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <PerformanceStatusPill
            status="at_risk"
            criteria="apply"
            expected={opening.target_applicant_count}
            actual={opening.applied_applicant_count}
          />

          <span className="size-6 hover:bg-fx-brand-100 inline-flex items-center justify-center rounded-sm">
            {isExpanded ? (
              <ChevronDownIcon className="size-4" aria-hidden />
            ) : (
              <ChevronRightIcon className="size-4" aria-hidden />
            )}
          </span>
        </div>
      </button>

      {isExpanded && opening.slug && (
        <div id={recommendationsPanelId} className="flex flex-col gap-2">
          <Typography variant="caption">
            <FormattedMessage
              defaultMessage="Recommendations"
              description="Section label above suggestion cards inside an expanded at-risk opening row"
            />
          </Typography>

          {/* General recommendation */}
          <Suspense fallback={<GeneralRecommendation.Skeleton />}>
            <GeneralRecommendation
              opening={openingDetails}
              hiringGoal={hiringGoalDetails}
            />
          </Suspense>

          <Suspense fallback={<BudgetRecommendation.Skeleton />}>
            <BudgetRecommendation
              opening={openingDetails}
              hiringGoal={hiringGoalDetails}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}

OpeningItem.Skeleton = function OpeningItemSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-2 py-2">
          <Skeleton className="h-5 w-60" />
          <div className="flex h-5 items-center gap-3">
            <Skeleton className="w-35 h-4" />
            <img src={GreyDotIcon} alt="Separator" className="size-3" />
            <Skeleton className="w-25 h-4" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PerformanceStatusPill.Skeleton />
          <Skeleton className="size-2" />
        </div>
      </div>
    </div>
  );
};
